import { AlertTriangle, CircleCheck, Info, OctagonAlert, type LucideIcon } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import type { StatusTone } from '../../status-colors.ts';
import { Button } from '../Button/Button.tsx';
import styles from './Toast.module.css';

export type ToastInput = {
  message: string;
  tone?: StatusTone;
  actionLabel?: string;
  onAction?: () => void;
  durationMs?: number;
};
type Toast = ToastInput & { id: number };
type ToastContextValue = { notify: (toast: ToastInput) => void; dismiss: (id: number) => void };

const ToastContext = createContext<ToastContextValue | null>(null);
const DEFAULT_DURATION_MS = 5000;

const TONE_ICONS: Readonly<Record<StatusTone, LucideIcon>> = {
  success: CircleCheck,
  warning: AlertTriangle,
  danger: OctagonAlert,
  info: Info,
  neutral: Info,
  brand: Info,
};

const ToneIcon = ({ tone }: { tone: StatusTone }) => {
  const Icon = TONE_ICONS[tone];
  return <Icon size={20} aria-hidden="true" className={styles[tone]} />;
};

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counter = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const notify = useCallback(
    (input: ToastInput) => {
      counter.current += 1;
      const id = counter.current;
      setToasts((current) => [...current, { ...input, id }]);
      window.setTimeout(() => {
        dismiss(id);
      }, input.durationMs ?? DEFAULT_DURATION_MS);
    },
    [dismiss],
  );

  const value = useMemo(() => ({ notify, dismiss }), [notify, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className={styles['region']} aria-live="polite" aria-atomic="false">
        {toasts.map((toast) => (
          <div key={toast.id} className={styles['toast']} role="status">
            <ToneIcon tone={toast.tone ?? 'info'} />
            <span className={styles['message']}>{toast.message}</span>
            {toast.actionLabel && toast.onAction ? (
              <Button
                variant="inverse"
                size="sm"
                onClick={() => {
                  toast.onAction?.();
                  dismiss(toast.id);
                }}
              >
                {toast.actionLabel}
              </Button>
            ) : null}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast requires ToastProvider');
  return context;
};
